import {Task} from "@traas/lib/src/api/dto/Task";
import {QuantityType, TASK_QUANTITY_TYPE_OTHER} from "@traas/lib/src/api/dto/QuantityType";
import {CanvasContext} from "./types";
import {getBlocksInCanvas} from "./canvas";
import {durationMinutesToSlots, slotToTime} from "./slots";

interface PlanningSuggestion {
  duration: number
  durationInMinutes: number
  slot: number
  startAt: Date
}
export function suggestPlanningForTask(context: CanvasContext, task: Task, quantityTypes: QuantityType[], teamId: string, date: Date, slot: number): PlanningSuggestion|null {
  const suggestedTaskDuration = durationMinutesToSlots(task.durationMinutes ?? getDefaultTaskDuration(task, quantityTypes))
  const willCollide = willTaskCollideWithOtherBlocks(context, task, teamId, date, slot, suggestedTaskDuration)
  if (task && !willCollide && slot < 44 - suggestedTaskDuration) {
    return {
      duration: suggestedTaskDuration,
      durationInMinutes: suggestedTaskDuration * 15,
      slot: slot,
      startAt: slotToTime(slot, date),
    }
  }
  // First, check if the given slot is in an empty space, and check how large that empty space is.
  if (isSlotFree(context, teamId, date, slot)) {
    const freeWindow = detectFreeWindow(context, teamId, date, slot)
    if (freeWindow !== null) {
      const startSlot = freeWindow.startSlot
      const endSlot = freeWindow.startSlot + freeWindow.nSlots
      for (let duration = suggestedTaskDuration; duration >= 1; duration--) {
        for (let timeslot = startSlot; timeslot <= (endSlot - duration); timeslot++) {
          const willCollide = willTaskCollideWithOtherBlocks(context, task, teamId, date, timeslot, duration)
          if (task && !willCollide) {
            return {
              duration: duration,
              durationInMinutes: duration * 15,
              slot: timeslot,
              startAt: slotToTime(timeslot, date),
            }
          }
        }
      }
    }
  }

  for (let duration = suggestedTaskDuration; duration >= 1; duration--) {
    const defaultTimeslot = slot >= 0 && slot < 44 - duration ? slot : 4
    for (let timeslot = defaultTimeslot; timeslot < (44 - duration); timeslot++) {
      const willCollide = willTaskCollideWithOtherBlocks(context, task, teamId, date, timeslot, duration)
      if (task && !willCollide) {
        return {
          duration: duration,
          durationInMinutes: duration * 15,
          slot: timeslot,
          startAt: slotToTime(timeslot, date),
        }
      }
    }
  }
  return null
}

function isSlotFree(context: CanvasContext, teamId: string, date: Date, slot: number): boolean {
  const blocks = getBlocksInCanvas(context, date, teamId)
  return blocks.every((block) => slot < block.slot || slot > (block.slot+block.duration)) // Check if block is NOT overlapping the slot
}

function detectFreeWindow(context: CanvasContext, teamId: string, date: Date, slot: number): {startSlot: number, nSlots: number}|null {
  let startSlot = null
  let nSlots = 0
  for (let i = 0; i < 44; i++) {
    if (! isSlotFree(context, teamId, date, i)) {
      if (i > slot) {
        break;
      }
      startSlot = null
      nSlots = 0
    } else {
      if (startSlot === null) {
        startSlot = i
        nSlots = 0
      }
      nSlots++
    }
  }
  if (startSlot === null) {
    return null
  }
  return {
    startSlot: startSlot, nSlots: nSlots,
  }
}

export function willTaskCollideWithOtherBlocks(context: CanvasContext, task: Task, teamId: string, date: Date, newTimeslot: number, newDuration: number): boolean {
  const otherBlocks = getBlocksInCanvas(context, date, teamId)
    .filter(b => b.id !== task.id) // Exclude the requested block
  return otherBlocks.some(b => {
    return b.slot <= (newTimeslot + newDuration - 1) && (b.slot + b.duration - 1) >= newTimeslot
  })
}

/**
 * The default is 120 minutes. However, for some quantity types, the duration can be "predicted" based on the quantity
 * amount of the task. This is done through a linear regression.
 * @param task
 * @param quantityTypes
 */
export function getDefaultTaskDuration(task: Task, quantityTypes: QuantityType[]): number {
  const type = task.quantityType ?? TASK_QUANTITY_TYPE_OTHER
  const quantityType = quantityTypes.find(qt => qt.name === type)

  if (quantityType !== undefined && task.quantityAmount !== null && canPredictDuration(quantityType)) {
    return predictDuration(quantityType, task.quantityAmount, 15)
  }
  return 120
}

/**
 * Check wether the data for a prediction through linear regression is availble on a quantity type.
 * @param quantityType
 */
export function canPredictDuration(quantityType: QuantityType): boolean {
  return quantityType.gradient !== null && quantityType.intercept !== null
}

/**
 * Calculate a prediction value for a given quantity based on its type through linear regression. Then round its value
 * to minutes.
 * @param quantityType
 * @param quantity
 * @param roundToMinutes
 */
export function predictDuration(quantityType: QuantityType, quantity: number, roundToMinutes = 1): number {
  if (quantityType.gradient === null || quantityType.intercept === null) {
    throw new Error('Cannot make prediction for quantityType '+quantityType.id)
  }
  return Math.round((quantityType.gradient * quantity + quantityType.intercept) / roundToMinutes) * roundToMinutes;
}
